// Renders the menu into the topbar: a rail of icon buttons plus a "⋯" that
// opens everything else in a sheet.
//
// The rail length is a policy CSS declares (--menu-primary-count) and JS
// enforces, because `:nth-child(n + calc(var(--x) + 1))` is not valid CSS —
// An+B does not accept custom properties. One matchMedia listener per
// breakpoint, not a resize handler: media queries fire only on an actual
// crossing, and inside an iframe they already resolve against the iframe's own
// viewport, which is the surface that matters here.
//
// MIGRATION CONTRACT — read before reordering App.start():
// MenuHost.mount() must run BEFORE `new Ui()`, `new AudioControls()`,
// `new Topbar()`, `new VariantPanel()` and `new DebugLook()`, all of which throw
// when their element is missing. The rail is where those elements now come from.
// Get this wrong and the app is a black screen, not a degraded menu.

import { LOCALES, type Locale } from '../../i18n';
import { setStoredLocale } from '../../i18n/resolveLocale';
import { MenuSheet, type SheetRow } from './menuSheet.ts';
import type { MenuContext, MenuItem } from './types.ts';

const OVERFLOW_ID = '__more';

export class MenuHost {
  private readonly rail: HTMLDivElement;
  private readonly sheet: MenuSheet;
  private items: MenuItem[] = [];
  private readonly buttons = new Map<string, HTMLButtonElement>();
  private readonly mediaQueries: MediaQueryList[] = [];
  private readonly onMediaChange = () => this.layout();
  private readonly onKeydown = (e: KeyboardEvent) => this.handleShortcut(e);
  private onLocaleChange: ((locale: Locale) => void) | null = null;

  constructor(private ctx: MenuContext) {
    this.rail = document.createElement('div');
    this.rail.className = 'menu-rail';
    this.sheet = new MenuSheet(ctx);
  }

  /**
   * Mounts the rail into #topbar and renders `items` in the same call.
   * Deliberately one call, not mount()+setItems(): every button has to exist
   * before the constructors that getElementById them, and a caller that
   * interleaved something between the two would fail in a way that looks like
   * a missing feature rather than an ordering bug.
   */
  mount(items: MenuItem[]): void {
    const tools = document.querySelector('.topbar__tools');
    if (!tools) {
      console.error('[menu] .topbar__tools missing — the chrome cannot mount');
      return;
    }
    tools.replaceChildren(this.rail);

    // The breakpoints --menu-primary-count switches on. Kept in sync with
    // main.css by hand; a mismatch costs a rail slot, not correctness.
    for (const query of [
      '(max-width: 400px)',
      '(min-width: 401px) and (max-width: 640px)',
      '(min-width: 900px)',
      '(min-width: 1200px)',
      '(max-height: 420px)',
    ]) {
      const mql = matchMedia(query);
      mql.addEventListener('change', this.onMediaChange);
      this.mediaQueries.push(mql);
    }
    document.addEventListener('keydown', this.onKeydown);
    this.setItems(items);
  }

  setItems(items: MenuItem[]): void {
    this.items = items;
    this.render();
  }

  /** Called by whoever owns locale state when the user switches language. */
  setLocaleChangeHandler(handler: (locale: Locale) => void): void {
    this.onLocaleChange = handler;
  }

  setContext(ctx: MenuContext): void {
    this.ctx = ctx;
    this.render();
  }

  /** Re-reads isVisible / isEnabled / isPressed without rebuilding the DOM. */
  refresh(): void {
    for (const item of this.items) {
      const btn = this.buttons.get(item.id);
      if (!btn) continue;
      if (item.isVisible) btn.classList.toggle('hidden', !item.isVisible(this.ctx));
      if (item.isEnabled) btn.disabled = !item.isEnabled(this.ctx);
      if (item.isPressed) {
        const pressed = item.isPressed(this.ctx);
        btn.setAttribute('aria-pressed', String(pressed));
        btn.setAttribute('aria-label', this.ariaFor(item, pressed));
      }
    }
    this.layout();
  }

  /** Exposed on window.__imersivo for the device-verification harness. */
  debugItems(): { id: string; tier: string; audience: string; inRail: boolean }[] {
    // `.is-overflowed` matters as much as `.hidden` here: an overflowed button
    // is still in the rail's DOM (deliberately — getElementById and .click()
    // have to keep working) but is display:none and reachable only through the
    // sheet. Counting it as "in the rail" made the harness's reachability check
    // pass vacuously on exactly the narrow viewports it exists to police.
    const railIds = new Set(
      [...this.rail.querySelectorAll<HTMLButtonElement>('button[data-menu-id]')]
        .filter((b) => !b.classList.contains('hidden') && !b.classList.contains('is-overflowed'))
        .map((b) => b.dataset.menuId!),
    );
    return this.items.map((i) => ({
      id: i.id,
      tier: i.tier,
      audience: i.audience,
      inRail: railIds.has(i.id),
    }));
  }

  private render(): void {
    this.rail.replaceChildren();
    this.buttons.clear();

    for (const item of this.items) {
      if (item.renderAs && item.renderAs !== 'rail') continue;
      const btn = this.renderButton(item);
      this.buttons.set(item.id, btn);
      this.rail.append(btn);
    }

    this.rail.append(this.renderOverflowButton());
    this.layout();
  }

  private renderButton(item: MenuItem): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'icon-button';
    // The legacy id is why deleting the hardcoded markup does not break the 19
    // modules that still call getElementById for their own button.
    if (item.legacyDomId) btn.id = item.legacyDomId;
    btn.dataset.menuId = item.id;

    const icon = document.createElement('span');
    icon.className = 'icon-button__icon';
    // Five modules rewrite this span's textContent to show state
    // (narrator.ts:120, autoTour.ts:213, copresence.ts:367, tiltControls.ts:131,
    // voiceCommands.ts:170), so the element must exist with this exact class.
    icon.textContent = item.icon;
    btn.append(icon);

    const pressed = item.isPressed?.(this.ctx);
    btn.setAttribute('aria-label', this.ariaFor(item, pressed ?? false));
    if (item.titleKey) btn.title = this.ctx.t(item.titleKey);
    // Only claim aria-pressed for entries we actually track. A legacy toggle
    // still writes its own, and a stale "false" here would fight it.
    if (pressed !== undefined) btn.setAttribute('aria-pressed', String(pressed));
    if (item.startsHidden) btn.classList.add('hidden');
    if (item.isEnabled && !item.isEnabled(this.ctx)) btn.disabled = true;

    // No listener for legacy entries — the module's own click handler is live.
    if (item.activate) {
      btn.addEventListener('click', () => void item.activate!(this.ctx));
    }
    return btn;
  }

  private renderOverflowButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'icon-button menu-rail__more';
    btn.dataset.menuId = OVERFLOW_ID;
    btn.setAttribute('aria-label', this.ctx.t('common.moreAria'));
    btn.setAttribute('aria-haspopup', 'dialog');
    const icon = document.createElement('span');
    icon.className = 'icon-button__icon';
    icon.textContent = '⋯';
    btn.append(icon);
    btn.addEventListener('click', () => this.openOverflow());
    return btn;
  }

  /**
   * Decides how many buttons stay in the rail. Everything past the cut is
   * `.is-overflowed` — still in the DOM (so getElementById and .click() keep
   * working for the legacy modules and their keyboard shortcuts) but not
   * displayed, and reachable through the sheet.
   */
  private layout(): void {
    const limit = this.primaryCount();
    const visible = this.items.filter((item) => {
      const btn = this.buttons.get(item.id);
      return btn && !btn.classList.contains('hidden');
    });

    // Tier order decides who wins a slot; priority breaks ties within a tier.
    const rank = { primary: 0, secondary: 1, overflow: 2 } as const;
    const eligible = visible
      .filter((i) => i.tier !== 'overflow')
      .sort((a, b) => rank[a.tier] - rank[b.tier] || a.priority - b.priority)
      .slice(0, limit);
    const keep = new Set(eligible.map((i) => i.id));

    let overflowed = 0;
    for (const item of this.items) {
      const btn = this.buttons.get(item.id);
      if (!btn) continue;
      const inRail = keep.has(item.id);
      btn.classList.toggle('is-overflowed', !inRail);
      if (!inRail && !btn.classList.contains('hidden')) overflowed++;
    }

    // Nothing to overflow → no "⋯". A single extra button is worse than none.
    const more = this.rail.querySelector<HTMLButtonElement>('.menu-rail__more');
    if (more) more.classList.toggle('hidden', overflowed === 0);
  }

  private primaryCount(): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--menu-primary-count');
    const n = Number.parseInt(raw.trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : 4;
  }

  private openOverflow(): void {
    const rows: SheetRow[] = [];
    for (const item of this.items) {
      const btn = this.buttons.get(item.id);
      if (!btn || btn.classList.contains('hidden')) continue;
      if (!btn.classList.contains('is-overflowed')) continue;
      const pressed = item.isPressed?.(this.ctx);
      rows.push({
        id: item.id,
        label: this.ctx.t(item.labelKey),
        icon: item.icon,
        ariaLabel: this.ariaFor(item, pressed ?? false),
        group: item.group,
        pressed,
        disabled: btn.disabled,
        // Route through the real button so legacy click handlers, analytics and
        // aria bookkeeping all run exactly as they do from the rail.
        onSelect: () => btn.click(),
      });
    }

    rows.push({
      id: 'language',
      label: this.ctx.t('menu.language.label'),
      icon: '🌐',
      ariaLabel: this.ctx.t('menu.language.aria'),
      group: 'acessibilidade',
      onSelect: () => this.openLanguageSheet(),
    });

    this.sheet.openWith(rows, this.ctx.t('common.more'));
  }

  private openLanguageSheet(): void {
    const rows: SheetRow[] = LOCALES.map((locale) => ({
      id: `lang-${locale}`,
      label: this.ctx.t(locale === 'en' ? 'language.en' : 'language.pt-BR'),
      icon: locale === 'en' ? '🇬🇧' : '🇧🇷',
      selected: locale === this.ctx.locale,
      onSelect: () => {
        setStoredLocale(locale);
        this.onLocaleChange?.(locale);
      },
    }));
    // Deferred so this sheet opens after the overflow sheet's close finishes.
    requestAnimationFrame(() => this.sheet.openWith(rows, this.ctx.t('menu.language.label')));
  }

  private ariaFor(item: MenuItem, pressed: boolean): string {
    if (pressed && item.ariaPressedKey) return this.ctx.t(item.ariaPressedKey);
    return item.ariaKey ? this.ctx.t(item.ariaKey) : this.ctx.t(item.labelKey);
  }

  private handleShortcut(e: KeyboardEvent): void {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const target = e.target as HTMLElement | null;
    if (target?.matches('input, textarea, [contenteditable]')) return;
    for (const item of this.items) {
      // Legacy entries keep their own keydown listener; binding here too would
      // fire the action twice per press.
      if (!item.shortcut || !item.activate) continue;
      if (e.key.toLowerCase() !== item.shortcut.toLowerCase()) continue;
      e.preventDefault();
      void item.activate(this.ctx);
      return;
    }
  }

  destroy(): void {
    document.removeEventListener('keydown', this.onKeydown);
    for (const mql of this.mediaQueries) mql.removeEventListener('change', this.onMediaChange);
    this.mediaQueries.length = 0;
    this.sheet.destroy();
    this.rail.remove();
  }
}
